import { cn } from '@/lib/utils';
import { PropsWithChildren, useEffect, useMemo, useState } from 'react';

import type { IStep } from '@chainlit/react-client';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@/components/ui/accordion';
import { useTranslation } from 'components/i18n';

interface Props {
  step: IStep;
  isRunning?: boolean;
}

export default function Step({
  step,
  children,
  isRunning
}: PropsWithChildren<Props>) {
  const { t } = useTranslation();
  const using = useMemo(() => {
    return isRunning && step.start && !step.end && !step.isError;
  }, [step, isRunning]);

  const hasContent = step.input || step.output || step.steps?.length;
  const isError = step.isError;
  const stepName = step.name;

  // Inline favicon for the currently-fetching source — backend
  // (_fetch_pages_with_progress in app.py) sets step.metadata.progress_domain
  // to the most-recently-completed domain on each step.update() and clears it
  // when the fetch loop finishes. We only render it while the step is still
  // running so the final saved row stays clean.
  const progressDomain = (step.metadata as any)?.progress_domain as
    | string
    | undefined;
  const ProgressFavicon =
    progressDomain && using ? (
      <img
        src={`https://www.google.com/s2/favicons?domain=${progressDomain}&sz=32`}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="h-3.5 w-3.5 ml-1 inline-block rounded-sm align-text-bottom"
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).style.display = 'none';
        }}
      />
    ) : null;

  // Detect a thinking step: backend marks new ones via metadata.thinking;
  // older transcripts (and any step the rename failed on) still surface via
  // the legacy "Thinking" / "Thoughts" names.
  const isThinking =
    step.metadata?.thinking === true ||
    stepName === 'Thinking' ||
    stepName === 'Thoughts';

  // Type pill — same idea as the admin page's THINKING/TOOL tags, but using
  // theme-aware Tailwind tokens (the `/10` background adapts to light/dark and
  // the explicit text colors keep the pill legible against the chat surface).
  // The arbitrary [-webkit-text-fill-color:currentColor] overrides the
  // loading-shimmer parent's transparent fill so the pill text stays visible
  // *during* a running step (when the shimmer is active).
  const tag = isThinking
    ? {
        label: 'THINKING',
        className:
          'bg-red-500/10 text-red-700 dark:bg-red-400/15 dark:text-red-300'
      }
    : step.type === 'tool'
      ? {
          label: 'TOOL',
          className:
            'bg-blue-500/10 text-blue-700 dark:bg-blue-400/15 dark:text-blue-300'
        }
      : null;
  const TagPill = tag ? (
    <span
      className={cn(
        'text-[0.6rem] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded-full leading-none [-webkit-text-fill-color:currentColor]',
        tag.className
      )}
    >
      {tag.label}
    </span>
  ) : null;

  // When a tag pill is present, the pill alone communicates the step kind —
  // the i18n "Used"/"Using" prefix becomes redundant (and reads awkwardly with
  // a dynamic thinking label like "Solve the math problem"). Just show the
  // raw step name in that case.
  const statusUsing = t('chat.messages.status.using');
  const statusUsed = t('chat.messages.status.used');
  const usingLabel = tag
    ? stepName
    : statusUsing
      ? `${statusUsing} ${stepName}`
      : stepName;
  const usedLabel = tag
    ? stepName
    : statusUsed
      ? `${statusUsed} ${stepName}`
      : stepName;

  const [openValue, setOpenValue] = useState<string>(
    step.defaultOpen ? step.id : ''
  );

  // Auto-collapse when step finishes if autoCollapse is set
  useEffect(() => {
    if (!using && step.autoCollapse) {
      setOpenValue('');
    }
  }, [using, step.autoCollapse]);

  // If there's no content, just render the status without accordion
  if (!hasContent) {
    return (
      <div className="flex flex-col flex-grow w-0">
        <p
          className={cn(
            'flex items-center gap-1.5 font-medium',
            isError && 'text-red-500',
            !using && 'text-muted-foreground',
            using && 'loading-shimmer'
          )}
          id={`step-${stepName}`}
        >
          {TagPill}
          <span>{using ? usingLabel : usedLabel}</span>
          {ProgressFavicon}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-grow w-0">
      <Accordion
        type="single"
        collapsible
        value={openValue}
        onValueChange={(val) => setOpenValue(val)}
        className="w-full"
      >
        <AccordionItem value={step.id} className="border-none">
          <AccordionTrigger
            className={cn(
              'flex items-center gap-1.5 justify-start transition-none p-0 hover:no-underline',
              isError && 'text-red-500',
              !using && 'text-muted-foreground hover:text-foreground',
              using && 'loading-shimmer'
            )}
            id={`step-${stepName}`}
          >
            {TagPill}
            <span>{using ? usingLabel : usedLabel}</span>
            {ProgressFavicon}
          </AccordionTrigger>
          <AccordionContent>
            <div className="flex-grow mt-4 ml-1 pl-4 border-l-2 border-primary">
              {children}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
